// PurgeConfirmModal.tsx — shared destructive-action confirm modal for
// CTSK-333 (Phase 3 of EPIC CTSK-385, Recycle Bin UI). Reused across all
// 6 resource tabs in RecycleBinPanel.tsx.
//
// Ship-critical UX per the brief: purge is irreversible (hard DELETE),
// so the confirm gate requires the operator to TYPE the row's own
// natural key (slug/name/key — whatever `RecycleBinPanel` passes as
// `confirmValue`) exactly, not just click a button. The Purge button
// stays disabled until the typed value matches. Modeled after the
// same "type the resource name to confirm" pattern reference the brief
// pointed at (CTSK-176's download-as-.env modal) — no literal shared
// component existed there to import (that modal is a one-off), so this
// is a fresh, resource-agnostic version of that pattern.

import { useState } from 'react';

export function PurgeConfirmModal({
  open,
  resourceLabel,
  rowLabel,
  confirmValue,
  onConfirm,
  onCancel,
  isPending = false,
  errorMessage = null,
}: {
  /** Whether the modal is visible. Callers should unmount/condition- render
   *  rather than pass `open=false` — this component always renders its
   *  overlay when mounted. */
  open: boolean;
  /** Human label for the resource type, e.g. "tag", "storage container". */
  resourceLabel: string;
  /** Display name shown in the warning banner, e.g. the tag's name. */
  rowLabel: string;
  /** The exact string the operator must type to enable the Purge button
   *  (the row's slug/name/key — same value the API's `confirmSlug` body
   *  field is checked against server-side). */
  confirmValue: string;
  onConfirm: () => void;
  onCancel: () => void;
  isPending?: boolean;
  errorMessage?: string | null;
}) {
  const [typed, setTyped] = useState('');

  if (!open) return null;

  const matches = typed === confirmValue;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Confirm purge of ${resourceLabel} "${rowLabel}"`}
      onClick={onCancel}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface-raised)', borderRadius: 'var(--radius-md)',
          border: '1px solid var(--danger-primary, #B3261E)', padding: 24, maxWidth: 440, width: '90%',
        }}
      >
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12,
            padding: '8px 12px', background: 'rgba(179, 38, 30, 0.08)', borderRadius: 6,
            color: 'var(--danger-primary, #B3261E)', fontSize: 13, fontWeight: 600,
          }}
        >
          ⚠ This permanently deletes this {resourceLabel}. It cannot be undone.
        </div>

        <h3 style={{ margin: '0 0 8px 0', fontSize: 16 }}>
          Purge {resourceLabel} &ldquo;{rowLabel}&rdquo;?
        </h3>
        <p style={{ margin: '0 0 14px 0', fontSize: 13, color: 'var(--text-secondary)' }}>
          This is a hard delete — unlike Restore, there is no undo. The row and its
          audit-trail final snapshot are the only record that remains. Type{' '}
          <code style={{ fontWeight: 600 }}>{confirmValue}</code> below to confirm.
        </p>

        <label style={{ display: 'block', fontSize: 12, fontWeight: 500, marginBottom: 4 }}>
          Type &ldquo;{confirmValue}&rdquo; to confirm
        </label>
        <input
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          autoFocus
          placeholder={confirmValue}
          style={{
            width: '100%', padding: '8px 10px', fontSize: 13, marginBottom: 12,
            border: `1px solid ${matches ? 'var(--success-primary, #2F6B4E)' : 'var(--border-strong)'}`,
            borderRadius: 4, fontFamily: 'var(--font-mono)',
          }}
        />

        {errorMessage && (
          <div style={{ fontSize: 12, color: 'var(--danger-primary, #B3261E)', marginBottom: 12 }}>
            {errorMessage}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" className="btn" onClick={onCancel} disabled={isPending}>
            Cancel
          </button>
          <button
            type="button"
            className="btn"
            onClick={onConfirm}
            disabled={!matches || isPending}
            style={{
              background: matches ? 'var(--danger-primary, #B3261E)' : undefined,
              color: matches ? '#fff' : undefined,
              borderColor: matches ? 'var(--danger-primary, #B3261E)' : undefined,
              cursor: !matches || isPending ? 'not-allowed' : 'pointer',
            }}
          >
            {isPending ? 'Purging…' : 'Purge permanently'}
          </button>
        </div>
      </div>
    </div>
  );
}
